import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { useCreateRole, useUpdateRole, useDeleteRole } from '../hooks'
import { rolesApi } from '../api'
import { useQuery } from '@tanstack/react-query'
import type { Role, Permission } from '@/types/entities'

const roleSchema = z.object({
    name: z.string().min(3, 'Role name must be at least 3 characters').max(50, 'Role name cannot exceed 50 characters'),
    description: z.string().max(255, 'Description cannot exceed 255 characters').optional(),
    permissionIds: z.array(z.number()).optional().default([]),
})

type RoleFormData = z.infer<typeof roleSchema>

interface RoleManagementDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    role?: Role | null
    mode?: 'create' | 'edit' | 'view'
}

export function RoleManagementDialog({
    open,
    onOpenChange,
    role,
    mode = 'create'
}: RoleManagementDialogProps) {
    const { t } = useTranslation()
    const [selectedPermissions, setSelectedPermissions] = useState<number[]>([])

    const createRoleMutation = useCreateRole()
    const updateRoleMutation = useUpdateRole()
    const deleteRoleMutation = useDeleteRole()

    // Fetch all available permissions
    const { data: permissionsResponse, isLoading: permissionsLoading } = useQuery({
        queryKey: ['permissions'],
        queryFn: () => rolesApi.getAllPermissions(),
        enabled: open,
    })

    const permissions = permissionsResponse?.data || []

    const form = useForm<RoleFormData>({
        resolver: zodResolver(roleSchema),
        defaultValues: {
            name: '',
            description: '',
            permissionIds: [],
        },
    })

    // Reset form when dialog opens/closes or role changes
    useEffect(() => {
        if (open) {
            if (role && (mode === 'edit' || mode === 'view')) {
                form.reset({
                    name: role.name,
                    description: role.description || '',
                    permissionIds: role.permissions?.map(p => p.id) || [],
                })
                setSelectedPermissions(role.permissions?.map(p => p.id) || [])
            } else {
                form.reset({
                    name: '',
                    description: '',
                    permissionIds: [],
                })
                setSelectedPermissions([])
            }
        }
    }, [open, role, mode, form])

    const onSubmit = async (data: RoleFormData) => {
        console.log('Form submission started:', { mode, data, selectedPermissions })
        try {
            if (mode === 'create') {
                console.log('Creating role with data:', { ...data, permissionIds: selectedPermissions })
                await createRoleMutation.mutateAsync({
                    ...data,
                    permissionIds: selectedPermissions,
                })
            } else if (mode === 'edit' && role) {
                console.log('Updating role:', { id: role.id, data: { ...data, permissionIds: selectedPermissions } })
                await updateRoleMutation.mutateAsync({
                    id: role.id,
                    data: {
                        ...data,
                        permissionIds: selectedPermissions,
                    },
                })
            }
            console.log('Form submission successful, closing dialog')
            onOpenChange(false)
        } catch (error) {
            console.error('Error saving role:', error)
            // You could add toast notifications here for better UX
        }
    }

    const handleDelete = async () => {
        if (role && mode === 'edit') {
            try {
                await deleteRoleMutation.mutateAsync(role.id)
                onOpenChange(false)
            } catch (error) {
                console.error('Error deleting role:', error)
            }
        }
    }

    const handlePermissionToggle = (permissionId: number) => {
        setSelectedPermissions(prev =>
            prev.includes(permissionId)
                ? prev.filter(id => id !== permissionId)
                : [...prev, permissionId]
        )
    }

    const isSystemRole = role && ['admin', 'user', 'viewer'].includes(role.name)
    const isLoading = createRoleMutation.isPending || updateRoleMutation.isPending || deleteRoleMutation.isPending

    // Group permissions by resource
    const groupedPermissions = permissions.reduce((acc, permission) => {
        const resource = permission.resource
        if (!acc[resource]) {
            acc[resource] = []
        }
        acc[resource].push(permission)
        return acc
    }, {} as Record<string, Permission[]>)

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl h-[850px] w-[90vw] flex flex-col">
                <DialogHeader className="flex-shrink-0 pb-4">
                    <DialogTitle>
                        {mode === 'create' && t('roles.createRole')}
                        {mode === 'edit' && t('roles.editRole')}
                        {mode === 'view' && t('roles.viewRole')}
                    </DialogTitle>
                    <DialogDescription>
                        {mode === 'create' && t('roles.createRoleDescription')}
                        {mode === 'edit' && t('roles.editRoleDescription')}
                        {mode === 'view' && t('roles.viewRoleDescription')}
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
                        <div className="flex-1 overflow-hidden">
                            <div className="grid grid-cols-1 gap-4 mb-4">
                                <FormField
                                    control={form.control}
                                    name="name"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>{t('roles.name')}</FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    disabled={mode === 'view' || isSystemRole}
                                                    placeholder={t('roles.namePlaceholder')}
                                                    className="w-full"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="description"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>{t('roles.description')}</FormLabel>
                                            <FormControl>
                                                <Textarea
                                                    {...field}
                                                    disabled={mode === 'view'}
                                                    placeholder={t('roles.descriptionPlaceholder')}
                                                    rows={2}
                                                    className="w-full resize-none"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <FormLabel>{t('roles.permissions')}</FormLabel>
                                    <Badge variant="secondary">
                                        {selectedPermissions.length} {t('roles.selected')}
                                    </Badge>
                                </div>

                                <div className="h-80 border rounded-md bg-muted/20 overflow-y-auto p-3">
                                    {permissionsLoading ? (
                                        <div className="text-center text-muted-foreground py-8">
                                            {t('common.loading')}...
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            {Object.entries(groupedPermissions).map(([resource, resourcePermissions]) => (
                                                <div key={resource} className="space-y-2">
                                                    <div className="flex items-center space-x-2">
                                                        <h4 className="font-semibold capitalize">
                                                            {t(`permissions.resources.${resource.toLowerCase()}`, { defaultValue: resource })}
                                                        </h4>
                                                        <Badge variant="outline" className="text-xs">
                                                            {resourcePermissions.length}
                                                        </Badge>
                                                    </div>
                                                    <div className="grid grid-cols-3 gap-2 ml-4">
                                                        {resourcePermissions.map((permission) => (
                                                            <div key={permission.id} className="flex items-center space-x-2 p-1 rounded hover:bg-muted/50">
                                                                <Checkbox
                                                                    id={`permission-${permission.id}`}
                                                                    checked={selectedPermissions.includes(permission.id)}
                                                                    onCheckedChange={() => handlePermissionToggle(permission.id)}
                                                                    disabled={mode === 'view'}
                                                                />
                                                                <label
                                                                    htmlFor={`permission-${permission.id}`}
                                                                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                                                                >
                                                                    {t(`permissions.actions.${permission.action.toLowerCase()}`, { defaultValue: permission.action })}
                                                                </label>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <hr className="my-2" />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <DialogFooter className="flex-shrink-0 flex justify-end items-center py-4 px-0 border-t bg-background">
                            <div className="flex space-x-3">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => onOpenChange(false)}
                                    disabled={isLoading}
                                >
                                    {mode === 'view' ? t('common.close') : t('common.cancel')}
                                </Button>
                                {mode !== 'view' && (
                                    <Button
                                        type="submit"
                                        disabled={isLoading}
                                        onClick={() => {
                                            console.log('Save button clicked')
                                            console.log('Form state:', form.formState)
                                            console.log('Form errors:', form.formState.errors)
                                            console.log('Form values:', form.getValues())
                                            console.log('Selected permissions:', selectedPermissions)
                                        }}
                                    >
                                        {isLoading ? t('common.saving') : t('common.save')}
                                    </Button>
                                )}
                            </div>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}
